/**
 * The save-recovery screen.
 *
 * A **screen**, not an alert, and that is the whole point. An alert invites a
 * reflexive dismissal, and this is the moment the app has to make good on its
 * promise that a finished effort is never silently lost. The user leaves with
 * the work either archived or explicitly still held on the device — never with
 * a dialog they tapped through without reading.
 *
 * "Keep on {device}" closes **without discarding**: the terminal snapshot stays
 * on disk, and the next launch offers the same idempotent retry.
 */

import {
  SAVE_RECOVERY_COPY,
  saveRecoveryBody,
  saveRecoveryKeepLocal,
} from "@ff/domain";
import { Button, SectionLabel, Text } from "@ff/ui";

import styles from "./SaveRecovery.module.css";

export interface SaveRecoveryProps {
  /** "this iPhone", "this Mac" — the sentence has to stay true on every device. */
  readonly deviceNoun: string;
  /** Why the last attempt did not confirm, in the store's own words. */
  readonly detail?: string;
  readonly isRetrying: boolean;
  readonly onRetry: () => void;
  /** Closes and leaves the effort held. Never discards. */
  readonly onKeepLocal: () => void;
}

export function SaveRecovery(props: SaveRecoveryProps): React.JSX.Element {
  return (
    <section className={styles.screen} aria-labelledby="save-recovery-title">
      <Text variant="caption" tone="secondary" uppercase>
        {SAVE_RECOVERY_COPY.eyebrow}
      </Text>
      <SectionLabel title={SAVE_RECOVERY_COPY.title} id="save-recovery-title" />
      <Text>{saveRecoveryBody(props.deviceNoun)}</Text>
      {props.detail === undefined ? null : (
        <Text variant="footnote" tone="secondary">
          {props.detail}
        </Text>
      )}

      <div className={styles.actions}>
        <Button onPress={props.onRetry} unavailable={props.isRetrying}>
          {SAVE_RECOVERY_COPY.retry}
        </Button>
        <Button variant="secondary" onPress={props.onKeepLocal}>
          {saveRecoveryKeepLocal(props.deviceNoun)}
        </Button>
      </div>
    </section>
  );
}
